import React, { FC } from 'react';
import {
  Avatar,
  IconButton,
  Link,
  Tooltip,
  Typography,
  Unstable_Grid2 as Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { DataTable, DataTableActions, DataTableColumn, SocialIcon } from 'components';
import { InfiniteQuery } from 'types/app';
import { AnalyticsProfile, Network } from 'api';
import { SortAction, SortParam } from 'components/DataTable';

import { getRoles, getTitles } from 'features/insights/helpers';
import theme from 'theme';
import { getAvatarSrc } from 'helpers';

interface Props extends InfiniteQuery<AnalyticsProfile> {
  sortBy: SortParam;
  onSort: SortAction;
}

const TopTable: FC<Props> = ({
  sentinelRef,
  results,
  isExhausted,
  isInitialLoad,
  sortBy,
  onSort,
}) => {
  const { t } = useTranslation();

  const pagination = {
    isInitialLoad,
    isExhausted,
    sentinelRef,
  };

  const actions: DataTableActions<AnalyticsProfile> = profile => {
    if (!profile.network_profile_url) {
      return null;
    }

    return (
      <Grid container alignItems="center" sx={{ marginRight: 1 }}>
        <Grid>
          <Tooltip title={t('insights:linkedin_profile')}>
            <IconButton target="_blank" href={profile.network_profile_url} size="large">
              <SocialIcon provider={Network.Linkedin} sx={{ color: theme.palette.grey[800] }} />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    );
  };

  const columns: DataTableColumn<AnalyticsProfile>[] = [
    {
      width: 280,
      headerCell: {
        render: t('common:name'),
      },
      bodyCell: {
        render: profile => {
          return (
            <Link target="_blank" href={profile.network_profile_url} underline="hover">
              <Grid container alignItems="center" spacing={3}>
                <Grid xs={3}>
                  <Avatar
                    src={getAvatarSrc(profile.network_profile_image, 56)}
                    style={{ width: 56, height: 56 }}
                  />
                </Grid>

                <Grid xs>
                  <Typography variant="subtitle2">{profile.name}</Typography>
                </Grid>
              </Grid>
            </Link>
          );
        },
      },
    },
    {
      width: 240,
      headerCell: {
        render: t('common:company'),
      },
      bodyCell: {
        render: profile => (
          <Grid container alignItems="center" spacing={2}>
            <Grid xs={3}>
              <Avatar
                variant="square"
                src={getAvatarSrc(profile.company.company_logo, 48)}
                style={{ height: 48, width: 48, borderRadius: 4 }}
              />
            </Grid>
            <Grid xs>
              <Typography>{profile.company.job_company_name}</Typography>
            </Grid>
          </Grid>
        ),
      },
    },
    {
      width: 240,
      headerCell: {
        render: t('common:email'),
      },
      bodyCell: {
        render: profile => {
          function handleClick() {
            void navigator.clipboard.writeText(profile.email_address);
          }

          return (
            <Tooltip title={profile.email_address}>
              <Typography noWrap onClick={handleClick}>
                {profile.email_address}
              </Typography>
            </Tooltip>
          );
        },
      },
    },
    {
      width: 150,
      headerCell: {
        render: t('common:title'),
      },
      bodyCell: {
        render: profile => <Typography>{getTitles(profile)}</Typography>,
      },
    },
    {
      width: 150,
      headerCell: {
        render: t('admin:role'),
      },
      bodyCell: {
        render: profile => <Typography>{getRoles(profile)}</Typography>,
      },
    },
    {
      width: 120,
      headerCell: {
        sort: sortBy,
        render: t('insights:engagements'),
      },
      bodyCell: {
        render: profile => (
          <Typography variant="subtitle2" align="center">
            {profile.engagement_summary.engagements}
          </Typography>
        ),
      },
    },
    {
      width: 150,
      headerCell: {
        render: t('insights:last_engagement'),
      },
      bodyCell: {
        render: profile => {
          const mom = moment.unix(profile.engagement_summary.last_engagement);
          return <Typography>{mom.format('l')}</Typography>;
        },
      },
    },
  ];

  return (
    <DataTable
      pagination={pagination}
      // @ts-ignore
      data={results}
      columns={columns}
      actions={actions}
      rowSelection={null}
      sortBy={sortBy}
      onSort={onSort}
    />
  );
};

export default TopTable;
